import { useCallback, useEffect, useRef, useState } from 'react'
import type { Paginated } from '@/api/types'

const STRING_FILTER_KEYS = new Set(['q', 'name', 'slug', 'search'])
const DEFAULT_LIMIT = 10
const DEFAULT_DEBOUNCE_MS = 300

type Fetcher<T, F> = (params: { limit: number; offset: number } & F) => Promise<Paginated<T>>

interface Options<F> {
  initialLimit?: number
  initialFilters?: F
  debounceMs?: number
  enabled?: boolean
}

interface InfinitePaginatedListResult<T, F> {
  items: T[]
  total: number
  loading: boolean
  loadingMore: boolean
  error: string | null
  hasMore: boolean
  filters: F
  setFilter: <K extends keyof F>(key: K, value: F[K]) => void
  loadMore: () => void
  reload: () => void
  patchItems: (updater: (items: T[]) => T[]) => void
  removeItems: (predicate: (item: T) => boolean) => void
}

export function useInfinitePaginatedList<T, F extends Record<string, unknown>>(
  fetcher: Fetcher<T, F>,
  options: Options<F> = {},
): InfinitePaginatedListResult<T, F> {
  const {
    initialLimit = DEFAULT_LIMIT,
    initialFilters = {} as F,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    enabled = true,
  } = options

  const [items, setItems] = useState<T[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<F>(initialFilters)
  const [appliedFilters, setAppliedFilters] = useState<F>(initialFilters)
  const [nextOffset, setNextOffset] = useState<number | null>(0)
  const [reloadTick, setReloadTick] = useState(0)

  const fetcherRef = useRef(fetcher)
  const appliedFiltersRef = useRef(appliedFilters)
  const nextOffsetRef = useRef<number | null>(nextOffset)
  const loadingMoreRef = useRef(false)
  const requestVersionRef = useRef(0)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  fetcherRef.current = fetcher
  appliedFiltersRef.current = appliedFilters
  nextOffsetRef.current = nextOffset

  useEffect(() => {
    if (!enabled) {
      setItems([])
      setTotal(0)
      setNextOffset(0)
      setLoading(false)
      setLoadingMore(false)
      return
    }

    const requestVersion = ++requestVersionRef.current
    setLoading(true)
    setLoadingMore(false)
    loadingMoreRef.current = false
    setError(null)

    const timerId = window.setTimeout(() => {
      void fetcherRef.current({ limit: initialLimit, offset: 0, ...appliedFilters })
        .then((page) => {
          if (requestVersionRef.current !== requestVersion) return
          setItems(page.results)
          setTotal(page.count)
          setNextOffset(page.next ? page.results.length : null)
          setLoading(false)
        })
        .catch((reason: unknown) => {
          if (requestVersionRef.current !== requestVersion) return
          setError(reason instanceof Error ? reason.message : 'Erro ao carregar dados')
          setLoading(false)
        })
    }, 0)

    return () => {
      window.clearTimeout(timerId)
      requestVersionRef.current += 1
    }
  }, [appliedFilters, enabled, initialLimit, reloadTick])

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
  }, [])

  const loadMore = useCallback(() => {
    const offset = nextOffsetRef.current
    if (!enabled || offset === null || loadingMoreRef.current) return

    loadingMoreRef.current = true
    setLoadingMore(true)
    setError(null)
    const requestVersion = requestVersionRef.current

    void fetcherRef.current({
      limit: initialLimit,
      offset,
      ...appliedFiltersRef.current,
    })
      .then((page) => {
        if (requestVersionRef.current !== requestVersion) return
        setItems((current) => [...current, ...page.results])
        setTotal(page.count)
        setNextOffset(page.next ? offset + page.results.length : null)
      })
      .catch((reason: unknown) => {
        if (requestVersionRef.current !== requestVersion) return
        setError(reason instanceof Error ? reason.message : 'Erro ao carregar dados')
      })
      .finally(() => {
        if (requestVersionRef.current !== requestVersion) return
        loadingMoreRef.current = false
        setLoadingMore(false)
      })
  }, [enabled, initialLimit])

  const setFilter = useCallback(
    <K extends keyof F>(key: K, value: F[K]) => {
      const nextFilters = { ...filters, [key]: value } as F
      setFilters(nextFilters)

      if (debounceRef.current) clearTimeout(debounceRef.current)
      const isDebounced = typeof key === 'string' && STRING_FILTER_KEYS.has(key)
      if (isDebounced) {
        debounceRef.current = setTimeout(() => setAppliedFilters(nextFilters), debounceMs)
      } else {
        setAppliedFilters(nextFilters)
      }
    },
    [debounceMs, filters],
  )

  const reload = useCallback(() => {
    setAppliedFilters(filters)
    setReloadTick((current) => current + 1)
  }, [filters])

  const patchItems = useCallback((updater: (current: T[]) => T[]) => {
    setItems((current) => updater(current))
  }, [])

  const removeItems = useCallback((predicate: (item: T) => boolean) => {
    setItems((current) => {
      const nextItems = current.filter((item) => !predicate(item))
      const removedCount = current.length - nextItems.length
      if (removedCount > 0) {
        setTotal((currentTotal) => Math.max(0, currentTotal - removedCount))
        setNextOffset((currentOffset) =>
          currentOffset === null ? null : Math.max(0, currentOffset - removedCount),
        )
      }
      return nextItems
    })
  }, [])

  return {
    items,
    total,
    loading,
    loadingMore,
    error,
    hasMore: nextOffset !== null && items.length < total,
    filters,
    setFilter,
    loadMore,
    reload,
    patchItems,
    removeItems,
  }
}
